import React, { useEffect, useMemo, useState } from "react";
import { Edit, Trash, FilterSearch } from "iconsax-react";
import beveatApiResourceOwner from "../../../api/resource_owner_beveat_api.js";
import { commonConfigs, urls } from "../../../../config.js";
import CertificateTemplateFilters from "./components/certificate_template_filter.jsx";
import CertificateTemplateTable from "./components/certificate_template_table.jsx";
import UploadCertificateTemplateModal from "./components/upload_certificate_template_modal.jsx";
import UpdateCertificateTemplateModal from "./components/update_certificate_template_modal.jsx";
import DeleteModal from "../../components/delete_modal.jsx";
import { CertificateSyscodeString, CERTIFICATE_SYSCODE } from "../../../constants/certificate_syscode.jsx";
import { CertificateProficiencySyscodeString, CERTIFICATE_PROFICIENCY_SYSCODE } from "../../../constants/certificate_proficiency_syscode.jsx";
import { TrainingProviderSyscodeString, TRAINING_PROVIDER_SYSCODE } from "../../../constants/training_provider_syscode.jsx";

// Helper functions to map filter values to syscodes
const getCourseworkSyscodes = (coursework) => {
  const courseworkMap = {
    "NETS QR (Web)": [
      CERTIFICATE_SYSCODE.SANDBOX_NETS_QR_Web,
      CERTIFICATE_SYSCODE.UAT_NETS_QR_Web,
      CERTIFICATE_SYSCODE.GO_LIVE_NETS_QR_Web
    ],
    "NETS QR (Mobile)": [
      CERTIFICATE_SYSCODE.SANDBOX_NETS_QR_Mobile,
      CERTIFICATE_SYSCODE.UAT_NETS_QR_Mobile,
      CERTIFICATE_SYSCODE.GO_LIVE_NETS_QR_Mobile
    ],
    "eNETS (Web)": [
      CERTIFICATE_SYSCODE.SANDBOX_eNETS_Web,
      CERTIFICATE_SYSCODE.UAT_eNETS_Web,
      CERTIFICATE_SYSCODE.GO_LIVE_eNETS_Web
    ],
    "eNETS (Mobile)": [
      CERTIFICATE_SYSCODE.UAT_eNETS_Mobile,
      CERTIFICATE_SYSCODE.GO_LIVE_eNETS_Mobile
    ],
    "NETS Click": [
      CERTIFICATE_SYSCODE.UAT_NETS_Click,
      CERTIFICATE_SYSCODE.GO_LIVE_NETS_Click
    ]
  };
  
  return courseworkMap[coursework] || [];
};

const getEnvironmentSyscodes = (environment) => {
  const environmentMap = {
    "Sandbox": [
      CERTIFICATE_SYSCODE.SANDBOX_NETS_QR_Web,
      CERTIFICATE_SYSCODE.SANDBOX_NETS_QR_Mobile,
      CERTIFICATE_SYSCODE.SANDBOX_eNETS_Web
    ],
    "UAT": [
      CERTIFICATE_SYSCODE.UAT_NETS_Click,
      CERTIFICATE_SYSCODE.UAT_NETS_QR_Web,
      CERTIFICATE_SYSCODE.UAT_NETS_QR_Mobile,
      CERTIFICATE_SYSCODE.UAT_eNETS_Web,
      CERTIFICATE_SYSCODE.UAT_eNETS_Mobile
    ],
    "Go Live": [
      CERTIFICATE_SYSCODE.GO_LIVE_NETS_Click,
      CERTIFICATE_SYSCODE.GO_LIVE_NETS_QR_Web,
      CERTIFICATE_SYSCODE.GO_LIVE_NETS_QR_Mobile,
      CERTIFICATE_SYSCODE.GO_LIVE_eNETS_Web,
      CERTIFICATE_SYSCODE.GO_LIVE_eNETS_Mobile
    ]
  };
  
  return environmentMap[environment] || [];
};

const getCombinedSyscodes = (coursework, environment) => {
  let syscodes = [];
  
  if (coursework !== "All" && environment !== "All") {
    const courseworkSyscodes = getCourseworkSyscodes(coursework);
    const environmentSyscodes = getEnvironmentSyscodes(environment);
    syscodes = courseworkSyscodes.filter(code => environmentSyscodes.includes(code));
  } else if (coursework !== "All") {
    syscodes = getCourseworkSyscodes(coursework);
  } else if (environment !== "All") {
    syscodes = getEnvironmentSyscodes(environment);
  }
  
  return syscodes;
};

const CertificateTemplateLayout = () => {
  const [load, setLoad] = useState(true);
  const [templatesData, setTemplatesData] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [apiSearchTerm, setApiSearchTerm] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    coursework: "All",
    environment: "All",
    trainingProvider: "All",
    proficiency: "All"
  });
  const [appliedFilters, setAppliedFilters] = useState({
    coursework: "All",
    environment: "All", 
    trainingProvider: "All",
    proficiency: "All"
  });

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const transformApiData = (apiData) => {
    if (!Array.isArray(apiData)) return [];
    
    return apiData.map((item) => {
      let coursework = "";
      let environment = "";
      
      const certType = item.certificate_type || "";
      if (certType) {
        const environmentMatch = certType.match(/\(([^)]+)\)/);
        if (environmentMatch) {
          environment = environmentMatch[1].trim();
          coursework = certType.replace(/\s*\([^)]+\)\s*/, '').trim();
        } else {
          coursework = certType.trim();
          environment = "Unknown";
        }
      }
      
      return {
        coursework: coursework,
        environment: environment,
        proficiency: item.certificate_proficiency_type || "",
        trainingProvider: item.training_provider_name || "",
        courseCode: item.course_code || "",
        templateUrl: item.certificate_img_url || "",
        certificate_template_id: item.certificate_template_id || item.certificate_id,
        _original: item
      };
    });
  };

  const handleDownloadTemplate = async (templateUrl) => {
    try {
      const response = await fetch(templateUrl);
      const blob = await response.blob();
      
      // Extract filename from URL
      const url = new URL(templateUrl);
      const filename = url.pathname.substring(url.pathname.lastIndexOf('/') + 1);
      
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Download failed. Please try again.");
    }
  };

  async function getCertificateTemplates() {
    let trainingProviderParam = "";
    let proficiencyParam = "";
    let certificateParam = "";
    
    if (appliedFilters.trainingProvider !== "All") {
      const trainingProviderKey = Object.keys(TrainingProviderSyscodeString).find(
        key => TrainingProviderSyscodeString[key] === appliedFilters.trainingProvider
      );
      if (trainingProviderKey) {
        trainingProviderParam = trainingProviderKey;
      }
    }
    
    if (appliedFilters.proficiency !== "All") {
      const proficiencyKey = Object.keys(CertificateProficiencySyscodeString).find(
        key => CertificateProficiencySyscodeString[key] === appliedFilters.proficiency
      );
      if (proficiencyKey) {
        proficiencyParam = proficiencyKey;
      }
    }
    
    const combinedSyscodes = getCombinedSyscodes(appliedFilters.coursework, appliedFilters.environment);
    if (combinedSyscodes.length > 0) {
      certificateParam = combinedSyscodes.join(',');
    }
    
    const getAllCertificateTemplatesUrl = 
      urls.resourceOwnerUrl + 
      commonConfigs.apiUrls.getAllCertificateTemplates(
        trainingProviderParam, 
        certificateParam, 
        proficiencyParam, 
        currentPage, 
        apiSearchTerm || ""
      );
    
    await beveatApiResourceOwner
      .get(getAllCertificateTemplatesUrl, {
        headers: commonConfigs.resourceOwnerApiHeader,
      })
      .then((res) => {
        let templatesArray = null;
        let totalPagesCount = 0;
        let totalRecordsCount = 0;
        
        if (res.data?.result?.data?.certificate_templates && Array.isArray(res.data.result.data.certificate_templates)) {
          templatesArray = res.data.result.data.certificate_templates;
          totalPagesCount = res.data.result.data.total_pages || 0;
          totalRecordsCount = res.data.result.data.total_records || 0;
        } else if (res.data?.result?.data && Array.isArray(res.data.result.data)) {
          templatesArray = res.data.result.data;
        }
        
        if (templatesArray && Array.isArray(templatesArray) && templatesArray.length > 0) {
          const transformedData = transformApiData(templatesArray);
          setTemplatesData(transformedData);
        } else {
          setTemplatesData([]);
        }
        
        setTotalPages(totalPagesCount);
        setTotalRecords(totalRecordsCount);
        setLoad(false);
      })
      .catch((err) => {
        console.log(err);
        setTemplatesData([]);
        setTotalPages(0);
        setTotalRecords(0);
        setLoad(false);
      });
  }

  useEffect(() => {
    getCertificateTemplates();
  }, [appliedFilters.trainingProvider, appliedFilters.proficiency, appliedFilters.coursework, appliedFilters.environment, currentPage, apiSearchTerm]);

  const courseworkOptions = useMemo(() => {
    return ["NETS QR (Web)", "NETS QR (Mobile)", "eNETS (Web)", "eNETS (Mobile)", "NETS Click"];
  }, []);

  const environmentOptions = useMemo(() => {
    return ["Sandbox", "UAT", "Go Live"];
  }, []);

  const trainingProviderOptions = useMemo(() => {
    return Object.values(TrainingProviderSyscodeString).filter(value => value !== "Reserved").sort();
  }, []);
  
  const proficiencyOptions = useMemo(() => {
    return Object.values(CertificateProficiencySyscodeString).filter(value => value !== "Reserved").sort();
  }, []);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleApply = () => {
    setAppliedFilters(filters);
    setCurrentPage(0);
  };

  const handleReset = () => {
    const resetFilters = {
      coursework: "All",
      environment: "All",
      trainingProvider: "All",
      proficiency: "All"
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setCurrentPage(0);
    setApiSearchTerm("");
    setSearchTerm("");
  };

  const handleAdvancedSearch = () => {
    setApiSearchTerm(searchTerm.trim());
    setCurrentPage(0);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAdvancedSearch();
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleUploadTemplate = () => {
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const handleUploadSuccess = async () => {
    await getCertificateTemplates();
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setEditingTemplate(null);
  };

  const handleUpdateSuccess = async () => {
    await getCertificateTemplates();
  };

  const handleDeleteTemplate = (template) => {
    setRowToDelete(template);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSuccess = async () => {
    await getCertificateTemplates();
    setIsDeleteModalOpen(false);
    setRowToDelete(null);
  };

  const columns = useMemo(() => {
    return [
      { Header: "Coursework", accessor: "coursework" },
      { Header: "Environment", accessor: "environment" },
      { Header: "Proficiency", accessor: "proficiency" },
      { Header: "Training Provider", accessor: "trainingProvider" },
      { Header: "Course Code", accessor: "courseCode" },
      {
        id: "viewTemplate",
        Cell: ({ row }) => (
          <button
            type="button"
            onClick={() => handleDownloadTemplate(row.original.templateUrl)}
            className="text-black hover:text-gray-700 underline font-medium"
          >
            View Template
          </button>
        ),
      },
      {
        id: "actions",
        Cell: ({ row }) => (
          <div 
            className="text-gray-900 flex justify-end space-x-3"
            style={{ paddingRight: isMobile ? "8px" : "15px" }}
          >
            <button
              type="button"
              onClick={() => handleEditTemplate(row.original)}
              style={{
                minHeight: isMobile ? "44px" : undefined,
                minWidth: isMobile ? "44px" : undefined,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobile ? "8px" : undefined,
              }}
              title="Edit template"
            >
              <Edit 
                aria-hidden="true" 
                className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'}`} 
              />
            </button>
            
            <button
              type="button"
              onClick={() => handleDeleteTemplate(row.original)}
              style={{
                minHeight: isMobile ? "44px" : undefined,
                minWidth: isMobile ? "44px" : undefined,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobile ? "8px" : undefined,
              }}
              title="Delete template"
            >
              <Trash 
                aria-hidden="true" 
                className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} text-black hover:text-gray-700`} 
              />
            </button>
          </div>
        ),
      },
    ];
  }, [isMobile]);

  if (load) {
    return (
      <div className="w-full bg-gray-100 min-h-screen">
        <div className="flex items-center justify-center" style={{ minHeight: "50vh" }}>
          <div className="text-center p-8">
            <div className={`animate-spin rounded-full border-4 border-blue-200 border-t-blue-600 mx-auto mb-4 ${
              isMobile ? 'h-12 w-12' : 'h-16 w-16'
            }`}></div>
            <h3 className={`font-medium text-gray-900 ${isMobile ? 'text-lg' : 'text-xl'}`}>
              Loading Certificate Templates
            </h3>
            <p className={`text-gray-600 ${isMobile ? 'text-sm' : 'text-base'}`}>
              Please wait while we fetch the templates data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-100 min-h-screen">
      <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
        {isMobile && (
          <div className="text-center mb-4">
            <h2 className="font-bold text-gray-900" style={{ fontSize: "20px", marginBottom: "0", lineHeight: "1.3" }}>
              Certificate Templates
            </h2>
          </div>
        )}

        <CertificateTemplateFilters
          filters={filters}
          courseworkOptions={courseworkOptions}
          environmentOptions={environmentOptions}
          trainingProviderOptions={trainingProviderOptions}
          proficiencyOptions={proficiencyOptions}
          isMobile={isMobile}
          handleFilterChange={handleFilterChange}
          handleApply={handleApply}
          handleReset={handleReset}
        />

        <div className="mb-6">
          <div className="border-b border-gray-200 flex justify-between items-center">
            <div></div>
            <div className="flex items-center mb-1">
              <label className="flex gap-x-2 items-center">
                <span className="text-gray-700 font-medium text-sm">Search: </span>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    className="px-3 py-2 pr-12 rounded-md border border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    placeholder={`Course Code...`}
                    style={{
                      minHeight: isMobile ? "44px" : "40px",
                      fontSize: "14px"
                    }}
                  />
                  <button
                    type="button"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 flex items-center justify-center p-2 bg-gray-200 text-gray-800 hover:text-gray-700 focus:outline-none rounded-md hover:bg-gray-100"
                    onClick={handleAdvancedSearch}
                    title="Search"
                    style={{
                      minHeight: isMobile ? "36px" : "32px",
                      minWidth: isMobile ? "36px" : "32px",
                    }}
                  >
                    <FilterSearch className="w-4 h-4" />
                  </button>
                </div>
              </label>
            </div>
          </div>
        </div>

        <CertificateTemplateTable
          columns={columns}
          filteredData={templatesData}
          isMobile={isMobile}
          handleUploadTemplate={handleUploadTemplate}
          pageChangeHandler={handlePageChange}
          totalData={totalPages}
          currentPage={currentPage}
          totalRecords={totalRecords}
        />

        {templatesData.length === 0 && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <h3 className={`font-medium text-gray-700 ${isMobile ? 'text-lg' : 'text-xl'} mb-2`}>
                  No Templates Found
                </h3>
                <p className={`text-gray-500 ${isMobile ? 'text-sm' : 'text-base'} mb-4`}>
                  {apiSearchTerm 
                    ? `No templates found for "${apiSearchTerm}"`
                    : "No templates found with the current filters"
                  }
                </p>
                {(appliedFilters.coursework !== "All" ||
                  appliedFilters.environment !== "All" ||
                  appliedFilters.trainingProvider !== "All" ||
                  appliedFilters.proficiency !== "All" ||
                  apiSearchTerm) && (
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        <UploadCertificateTemplateModal
          isOpen={isUploadModalOpen}
          onClose={handleCloseUploadModal}
          onUploadSuccess={handleUploadSuccess}
        />

        <UpdateCertificateTemplateModal
          isOpen={isUpdateModalOpen}
          onClose={handleCloseUpdateModal}
          onUpdateSuccess={handleUpdateSuccess}
          templateData={editingTemplate}
        />

        {isDeleteModalOpen && (
          <DeleteModal
            setDeleteModal={setIsDeleteModalOpen}
            load={handleDeleteSuccess}
            url={urls.resourceOwnerUrl + commonConfigs.apiUrls.deleteCertificateTemplate(rowToDelete.certificate_template_id)}
          />
        )}
      </div>
    </div>
  );
};

export default CertificateTemplateLayout;